import { useEffect, useState } from "react";
import { AppDialog, DialogActionsBar, DialogField, DialogGrid2 } from "@/components/app-dialog";
import { FuiDropdown, FuiInput, FuiTextarea } from "@/components/fui-control";
import type { IntakeItem, IntakePriority, IntakeSource, IntakeType } from "@/engine/types";
import {
  INTAKE_PRIORITIES,
  INTAKE_PRIORITY_LABEL,
  INTAKE_SOURCE_LABEL,
  INTAKE_SOURCES,
  INTAKE_TYPE_LABEL,
  INTAKE_TYPES,
} from "@/store/intake-ops";
import { useProjectStore } from "@/store/project-store";

const sourceOptions = INTAKE_SOURCES.map((v) => ({ value: v, label: INTAKE_SOURCE_LABEL[v] }));
const typeOptions = INTAKE_TYPES.map((v) => ({ value: v, label: INTAKE_TYPE_LABEL[v] }));
const priorityOptions = INTAKE_PRIORITIES.map((v) => ({ value: v, label: INTAKE_PRIORITY_LABEL[v] }));

/** 요구를 일정에 넣기 전에 접수만 남긴다. */
export function QuickIntakeModal({ open, onClose, seed }: { open: boolean; onClose: () => void; seed?: Partial<IntakeItem> }) {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [source, setSource] = useState<IntakeSource>(seed?.source ?? "internal");
  const [type, setType] = useState<IntakeType>(seed?.type ?? "newScope");
  const [priority, setPriority] = useState<IntakePriority>("medium");
  const [organization, setOrganization] = useState("");

  useEffect(() => {
    if (!open) return;
    setTitle("");
    setDescription("");
    setOrganization("");
    setPriority("medium");
    setSource(seed?.source ?? "internal");
    setType(seed?.type ?? "newScope");
  }, [open, seed]);

  const submit = () => {
    if (!title.trim()) return;
    useProjectStore.getState().addIntake({
      ...seed,
      title: title.trim(),
      description: description.trim(),
      source,
      type,
      priority,
      ...(organization.trim() ? { organization: organization.trim() } : {}),
    });
    onClose();
  };

  return (
    <AppDialog
      open={open}
      onClose={onClose}
      title="요구 접수"
      width="md"
      footer={<DialogActionsBar onCancel={onClose} cancelLabel="취소" confirmLabel="접수" disabled={!title.trim()} onConfirm={submit} />}
    >
      <DialogField label="제목" required>
        <FuiInput density="dialog" value={title} onChange={(_, d) => setTitle(d.value)} autoFocus onKeyDown={(e) => e.key === "Enter" && submit()} />
      </DialogField>
      <DialogField label="내용">
        <FuiTextarea value={description} onChange={(_, d) => setDescription(d.value)} rows={4} />
      </DialogField>
      <DialogGrid2>
        <DialogField label="출처">
          <FuiDropdown density="dialog" value={source} options={sourceOptions} onValueChange={(v) => setSource(v as IntakeSource)} />
        </DialogField>
        <DialogField label="종류">
          <FuiDropdown density="dialog" value={type} options={typeOptions} onValueChange={(v) => setType(v as IntakeType)} />
        </DialogField>
      </DialogGrid2>
      <DialogGrid2>
        <DialogField label="우선순위">
          <FuiDropdown density="dialog" value={priority} options={priorityOptions} onValueChange={(v) => setPriority(v as IntakePriority)} />
        </DialogField>
        {source !== "internal" ? (
          <DialogField label="요청한 곳">
            <FuiInput density="dialog" value={organization} onChange={(_, d) => setOrganization(d.value)} />
          </DialogField>
        ) : (
          <span />
        )}
      </DialogGrid2>
    </AppDialog>
  );
}
